import { useEffect } from 'react';
import { Form } from 'antd';
import Modal from '@/components/ModalConfirm';
import useModal from '@keyslabor/shared/hooks/useModal';
import { Box, Divider, Text } from '@keyslabor/shared/ui';
import { DatePicker } from '@/components/DatePicker';
import { Select } from '@keyslabor/shared/ui/Primitives/Select';
import { Input } from '@keyslabor/shared/ui/Primitives/Input';
import { format } from 'date-fns';
import { useIsMobile } from '@keyslabor/shared/hooks/useBreakPoint';
import { useCompanyInfoQuery } from '@/api/methods/useCompanyInfoQuery.ts';
import { useCreateLostHoursMutation } from '@/api/methods/useCreateLostHoursMutation.ts';
import { useEmployeeListOptions } from '@/hooks/api/useEmployeesList.ts';

interface FormFields {
  employee: number;
  date: Date;
  hours: string;
}

export default function useCreateLostHour() {
  const isMobile = useIsMobile();
  const [form] = Form.useForm<FormFields>();
  const { data: companyInfo } = useCompanyInfoQuery();
  const employeeList = useEmployeeListOptions();

  const { onToggle, onClose, modalProps } = useModal({
    onHide: () => {
      form.resetFields();
    },
  });

  const { mutateAsync: createLostHours } = useCreateLostHoursMutation();

  const onFinish = async (values: FormFields) => {
    await createLostHours({
      ...values,
      date: format(values?.date, 'yyyy-MM-dd'),
      created_by: companyInfo?.manager?.id ?? 0,
    });
  };

  useEffect(() => {
    if (isMobile) {
      onClose();
    }
  }, [isMobile, onClose]);

  const onFinishFailed = (errorInfo: unknown) => {
    console.error(errorInfo);
  };

  return [
    <Modal
      key={'create-lost-hour'}
      textOk="Submit"
      titleSuccess="Lost Hours added"
      textSuccessOk="OK"
      footerProps={{
        vertical: isMobile,
        divider: !isMobile,
        buttonProps: {
          kind: isMobile ? 'normal' : 'modal',
        },
      }}
      onSuccessOk={() => {
        onClose();
        form.resetFields();
      }}
      destroyOnClose={true}
      textClose="Cancel"
      callback={async () => {
        await form.validateFields();
        return await onFinish(form.getFieldsValue());
      }}
      onCancel={() => {
        form.resetFields();
        onClose();
      }}
      {...modalProps}
    >
      <Box width="100%" key="lost-hours-container">
        <>
          <Text
            maxWidth={isMobile ? '248px' : '338px'}
            fontSize="16px"
            fontWeight="500"
            lineHeight="18.75px"
            textAlign="center"
            display="flex"
            margin="auto"
          >
            If you can’t find employee on list to add lost hours please contact
            agency
          </Text>
          <Divider margin="20px 0px" />
          <Box width="100%" mb="20px">
            <Text
              maxWidth="338px"
              fontWeight="700"
              lineHeight="16.41px"
              fontSize="14px"
            >
              Add lost hours:
            </Text>
          </Box>

          <Form
            form={form}
            name="create-lost-hour-prop"
            style={{ width: '100%', alignItems: 'center' }}
            initialValues={{
              employee: undefined,
              date: new Date(),
              hours: '',
            }}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            autoComplete="off"
            key="create-lost-hour-prop"
          >
            <Form.Item
              name="employee"
              style={{ width: '100%', margin: '0px 0px 10px 0px' }}
              rules={[{ required: true, message: 'Please input employee!' }]}
            >
              <Select
                height={'40px'}
                name="employee"
                label="Employee"
                placeholder={'------------'}
                color={'#20212480'}
                required
                showSearch={true}
                onSearch={() => null}
                options={employeeList?.length ? employeeList : []}
              />
            </Form.Item>

            <Form.Item
              name="date"
              style={{
                width: '100%',
                margin: '0px 0px 10px 0px',
              }}
              rules={[{ required: true, message: 'Please input date!' }]}
            >
              <DatePicker
                name="Date"
                label="Date"
                required
                height="40px"
                onFocus={true}
                onBlur={true}
                maxDate={new Date()}
              />
            </Form.Item>

            <Form.Item
              name="hours"
              style={{ width: '100%', margin: '0px 0px 10px 0px' }}
              rules={[
                { required: true, message: 'Please input hours!' },
                {
                  validator: (_, value) => {
                    const [numbers, decimals] = value.split('.');
                    if (decimals?.length > 2) {
                      return Promise.reject(
                        'Only two decimal numbers are allowed'
                      );
                    }
                    return Promise.resolve();
                  },
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (value <= 16) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error(
                        'Ensure this value is less then or equal to 16 !'
                      )
                    );
                  },
                }),
              ]}
            >
              <Input
                width="100%"
                height="40px"
                fontSize="14px"
                name="hours"
                required
                label="Hours"
                borderColor="#7F87B6"
                isNumber={true}
              />
            </Form.Item>
          </Form>
          <Text fontSize="10px" color="#7F87B6" fontWeight="500">
            Attention! For example 2.5 hours equal to 2 hours 30 minutes, not 2
            hours 50 minutes
          </Text>
        </>
      </Box>
    </Modal>,
    () => {
      onToggle();
    },
  ] as const;
}
